import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { Child } from '../api/client'
import { client } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { EmptyStateBox } from './EmptyStateBox'

export function ChildrenBox() {
  const { t } = useTranslation('children')
  const { parent } = useAuthStore()
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchChildren = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await client.get('/children/')
        // Ensure we have unique children by ID
        const uniqueChildren = response.data.reduce((acc: Child[], child: Child) => {
          // Check if we already have this child in the accumulator
          if (!acc.some(c => c.id === child.id)) {
            acc.push(child)
          }
          return acc
        }, [])
        setChildren(uniqueChildren || [])
      } catch (err) {
        setError(t('errors.loadFailed'))
      } finally {
        setLoading(false)
      }
    }

    if (parent?.id) {
      fetchChildren()
    } else {
      setLoading(false)
    }
  }, [parent?.id, t])

  return (
    <div className="card-neobrutalism h-full flex flex-col">
      <div className="flex items-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
        <h2 className="text-2xl font-black text-neobrutalism-text">
          {t('childrenBox.title')}
        </h2>
      </div>

      {error && (
        <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
          <p className="text-neobrutalism-text font-bold">{error}</p>
        </div>
      )}

      <div className="flex-grow mb-6">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neobrutalism-accent"></div>
          </div>
        ) : children.length > 0 ? (
          <ul className="space-y-4">
            {children.map((child) => (
              <li key={child.id} className={`hover:translate-y-[-2px] border-4 ${child.gender === 'female' ? 'border-pink-100' : 'border-blue-100'} p-4`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex items-center">
                    <img 
                      src={`/images/${child.gender === 'male' ? 'male.png' : 'female.png'}`} 
                      alt={child.gender} 
                      className="w-12 h-12 mr-4 flex-shrink-0"
                    />
                    <div className="w-full">
                      <p className="font-bold text-lg">{`${child.first_name} ${child.last_name}`}</p>
                      <div className="block sm:flex sm:items-center sm:gap-2">
                        <p className="text-sm font-medium mb-1 sm:mb-0">{t('form.registrationNumber')}:</p>
                        <p className="text-sm">{child.registration_number}</p>
                      </div>
                      <div className="block sm:flex sm:items-center sm:gap-2 mt-2 sm:mt-0">
                        <p className="text-sm font-medium mb-1 sm:mb-0">{t('form.birthDate')}:</p>
                        <p className="text-sm">
                          {(() => {
                            const date = new Date(child.birth_date);
                            const day = date.getDate().toString().padStart(2, '0');
                            const month = (date.getMonth() + 1).toString().padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}/${month}/${year}`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="self-start sm:self-center mt-2 sm:mt-0 w-full sm:w-auto">
                    <Link
                      to={`/children/${child.id}`}
                      className={`btn-neobrutalism text-center block w-full sm:w-auto py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base ${child.gender === 'female' ? 'bg-neobrutalism-pink' : 'bg-neobrutalism-blue'}`}
                    >
                      {t('childrenBox.viewButton')}
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyStateBox
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            text={t('childrenBox.noChildren')}
            bgColor="bg-pink-50"
            borderColor="border-pink-200"
            iconBgColor="bg-pink-100"
            iconColor="text-pink-600"
          />
        )}
      </div>

      <Link
        to="/children/add"
        className="btn-neobrutalism text-center bg-neobrutalism-accent w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
      >
        {t('childrenBox.addChild')}
      </Link>
    </div>
  )
} 